import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Users, FileText, FileCheck, Banknote, CalendarCheck, CreditCard, FolderKanban, BarChart3, UserCog, Settings, Menu, LogOut, Search, Bell, Link2, Crown, CalendarClock, Route, Building2, MessageCircle, Trash2, RefreshCw, Eye, EyeOff, Loader2, ExternalLink, Handshake, FlaskConical, KeyRound, KanbanSquare, Smartphone } from "lucide-react";
import { PortalLinkDialog } from "@/components/PortalLinkDialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AvisosApp, InstrucoesInstalacao } from "@/components/AvisosApp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIdentidade } from "@/lib/identidade";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import GlobalSearch from "@/components/GlobalSearch";
import InstallPWA from "@/components/InstallPWA";
import logoDark from "@/assets/logo-dark-t.png";
const logoDarkT = logoDark;
import { dispatchRefresh } from "@/lib/refresh";
import logoLight from "@/assets/logo-light.png";
import { toast } from "sonner";
import { fmtDate } from "@/lib/api";

const ICONS = {
  LayoutDashboard, Users, FileText, FileCheck, Banknote, CalendarCheck,
  CreditCard, FolderKanban, BarChart3, UserCog, Settings, Menu, CalendarClock, Route, Building2, MessageCircle, Crown, Handshake, FlaskConical, KeyRound, KanbanSquare,
};

const NAV = [
  { label: "Dashboard", icon: "LayoutDashboard", path: "/dashboard", testid: "sidebar-nav-dashboard" },
  { label: "Cobrança do Dia", icon: "CalendarClock", path: "/cobranca-diaria", testid: "sidebar-nav-cobranca" },
  { label: "Rota de Cobrança", icon: "Route", path: "/rota-cobranca", testid: "sidebar-nav-rota" },
  { label: "Clientes", icon: "Users", path: "/clientes", testid: "sidebar-nav-clientes" },
  { label: "Solicitações", icon: "FileText", path: "/solicitacoes", testid: "sidebar-nav-solicitacoes" },
  { label: "Atendimentos", icon: "KanbanSquare", path: "/atendimentos", testid: "sidebar-nav-atendimentos", noAgente: true },
  { label: "Contratos", icon: "FileCheck", path: "/contratos", testid: "sidebar-nav-contratos" },
  { label: "Simular Contrato", icon: "FlaskConical", path: "/simular-contrato", testid: "sidebar-nav-simular" },
  { label: "Empréstimos", icon: "Banknote", path: "/emprestimos", testid: "sidebar-nav-emprestimos" },
  { label: "Parcelas", icon: "CalendarCheck", path: "/parcelas", testid: "sidebar-nav-parcelas" },
  { label: "Pagamentos", icon: "CreditCard", path: "/pagamentos", testid: "sidebar-nav-pagamentos" },
  { label: "Acordos", icon: "Handshake", path: "/acordos", testid: "sidebar-nav-acordos" },
  { label: "Mensagens", icon: "MessageCircle", path: "/mensagens", testid: "sidebar-nav-mensagens", noAgente: true },
  { label: "Documentos", icon: "FolderKanban", path: "/documentos", testid: "sidebar-nav-documentos" },
  { label: "Relatórios", icon: "BarChart3", path: "/relatorios", testid: "sidebar-nav-relatorios", noAgente: true },
  { label: "Portal", icon: "KeyRound", path: "/portal-admin", testid: "sidebar-nav-portal-admin", portalAdmin: true },
  { label: "Usuários", icon: "UserCog", path: "/usuarios", testid: "sidebar-nav-usuarios", adminOnly: true },
  { label: "Configurações", icon: "Settings", path: "/configuracoes", testid: "sidebar-nav-configuracoes", noAgente: true },
];
const AGENTE_PATHS = new Set(["/dashboard", "/cobranca-diaria", "/rota-cobranca", "/clientes", "/contratos", "/parcelas", "/pagamentos", "/acordos", "/documentos"]);
export const navVisivel = (n, user) => {
  const role = user?.role;
  if (role === "agente_campo") return AGENTE_PATHS.has(n.path);
  if (n.adminOnly) return role === "administrador";
  if (n.portalAdmin) return role === "administrador" || user?.acesso_portal_admin;
  return true;
};

const MOBILE_NAV = [
  { label: "Início", icon: "LayoutDashboard", path: "/dashboard", testid: "mobile-nav-inicio" },
  { label: "Cobrança", icon: "CalendarClock", path: "/cobranca-diaria", testid: "mobile-nav-cobranca" },
  { label: "Clientes", icon: "Users", path: "/clientes", testid: "mobile-nav-clientes" },
  { label: "Parcelas", icon: "CalendarCheck", path: "/parcelas", testid: "mobile-nav-parcelas" },
];

function Brand({ small, onLight, header, sheet }) {
  const { user } = useAuth();
  const ident = useIdentidade();
  const emp = user?.empresa;
  const trt = <img src={ident.src("imagem_entrada", logoDarkT)} alt="TRT GESTÃO — Gerenciamento de Contratos" className={header ? "h-32 w-auto max-w-[16rem] object-contain" : "h-24 w-auto object-contain rounded-xl"} />;
  if (header) return trt;
  if (sheet) {
    return (
      <div className="flex flex-col items-center gap-1 min-w-0 w-full">
        <div className="rounded-xl px-3 py-1" style={{ background: "var(--fundo-marca, #000617)" }}>{trt}</div>
        {emp && emp.slug !== "trt" && <div className="text-white text-sm font-semibold truncate max-w-full" data-testid="sheet-brand-name">{emp.nome}</div>}
      </div>
    );
  }
  if (emp?.logo) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/company/logo?e=${emp.id}`} alt={emp.nome}
          className={`${small ? "h-9" : "h-12"} w-auto max-w-[200px] object-contain`} crossOrigin="use-credentials" />
      </div>
    );
  }
  if (emp && emp.slug !== "trt") {
    return <div className={`font-extrabold tracking-tight truncate min-w-0 ${onLight ? "text-onyx" : "text-white"} ${small ? "text-sm sm:text-base" : "text-lg"}`} title={emp.nome} data-testid="brand-name">{emp.nome}</div>;
  }
  if (header) {
    return trt;
  }
  return (
    <img
      src={small ? ident.src("icone", logoLight) : ident.src("imagem_entrada", logoDark)}
      alt="TRT GESTÃO — Gerenciamento de Contratos"
      className={small ? "h-10 w-auto rounded-lg" : "h-24 w-auto rounded-xl"}
    />
  );
}

function InstalarDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm w-[calc(100vw-2rem)] rounded-2xl" data-testid="instalar-dialog">
        <DialogHeader><DialogTitle>Como instalar o aplicativo</DialogTitle></DialogHeader>
        <InstrucoesInstalacao />
      </DialogContent>
    </Dialog>
  );
}

function SidebarLinks({ role, onNavigate, platformRole, user }) {
  const items = platformRole === "superadmin"
    ? [{ label: "Painel Master", icon: "Crown", path: "/master", testid: "sidebar-nav-empresas" }, ...NAV]
    : NAV;
  return (
    <nav className="flex flex-col gap-1 mt-2">
      {items.filter((n) => n.path === "/master" || navVisivel(n, user)).map((item) => {
        const Icon = ICONS[item.icon];
        return (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-gold text-black shadow-sm"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [privacy, setPrivacy] = useState(() => sessionStorage.getItem("trt_privacy") === "1");
  useEffect(() => {
    document.body.classList.toggle("privacy-on", privacy);
    sessionStorage.setItem("trt_privacy", privacy ? "1" : "0");
  }, [privacy]);
  // Não remonta a página (key) — isso reiniciava formulários/modais abertos (ex.: baixa) em celular/tablet ao puxar a tela.
  const doRefresh = () => {
    if (document.querySelector("[role=dialog]")) return;
    setRefreshing(true);
    dispatchRefresh();
    loadNotif();
    setTimeout(() => setRefreshing(false), 900);
  };
  const pull = React.useRef({ y: 0, active: false });
  const onTouchStart = (e) => { pull.current = { y: e.touches[0].clientY, active: window.scrollY <= 0 }; };
  const onTouchEnd = (e) => {
    if (pull.current.active && e.changedTouches[0].clientY - pull.current.y > 90 && window.scrollY <= 0) doRefresh();
    pull.current.active = false;
  };
  const [notif, setNotif] = useState({ items: [], nao_lidas: 0 });
  const [moreOpen, setMoreOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [instalarOpen, setInstalarOpen] = useState(false);

  const loadNotif = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotif(data);
    } catch { /* ignorado */ }
  };

  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 30000);
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearInterval(t);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const generateLink = async () => {
    const { data } = await api.post("/applications/generate-link");
    const url = `${window.location.origin}${data.url}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link de análise copiado!", { description: url, action: { label: "Abrir", onClick: () => window.open(url, "_blank", "noopener") } });
  };

  const markRead = async () => {
    await api.post("/notifications/read");
    loadNotif();
  };
  const clearNotif = async () => {
    await api.delete("/notifications");
    loadNotif();
  };
  const apagar = async (n) => {
    if (!window.confirm("Apagar esta notificação? Só o aviso é removido; solicitação, contrato e documentos permanecem.")) return;
    try { await api.delete(`/notifications/${n.id}`); } catch { /* já removida */ }
    loadNotif();
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <AvisosApp />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 top-36 w-64 bg-onyx border-r border-white/5 p-4 z-40 slim-scroll overflow-y-auto">
        {user?.empresa?.nome && user.empresa.slug !== "trt" && <div className="px-3 pt-1 pb-3 text-[11px] uppercase tracking-wider text-slate-400 truncate" title={user.empresa.nome} data-testid="sidebar-empresa-nome">{user.empresa.nome}</div>}
        <SidebarLinks user={user} role={user?.role} platformRole={user?.platform_role} />
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-1 py-2">
            <Avatar className="w-9 h-9 border border-gold/40">
              <AvatarFallback className="bg-gold text-black text-xs font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="leading-tight overflow-hidden">
              <div className="text-white text-xs font-semibold truncate">{user?.name}</div>
              <div className="gold-text text-[10px] capitalize">{user?.role}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-50 h-16 lg:h-36 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 lg:px-6 bg-white/85 backdrop-blur-md border-b border-slate-200 safe-x app-header lg:border-white/10 lg:-ml-64 lg:pl-4" style={{ "--header-bg": "var(--fundo-marca, #000617)" }}>
          <button data-testid="header-menu-btn" onClick={() => setMoreOpen(true)} className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-700" aria-label="Abrir menu">
            <Menu className="w-6 h-6" />
          </button>
          <div className="lg:hidden min-w-0 flex-1 text-sm font-semibold text-slate-700 truncate" data-testid="header-empresa-nome">{user?.empresa?.nome && user.empresa.slug !== "trt" ? user.empresa.nome : ""}</div>
          <div className="hidden lg:flex items-center justify-center h-36 w-64 shrink-0 mr-4" data-testid="header-brand"><Brand header /></div>
          <button
            data-testid="header-search-btn"
            onClick={() => setSearchOpen(true)}
            className="ml-auto lg:ml-0 lg:mr-auto shrink-0 flex items-center gap-2 text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg px-2.5 sm:px-3 py-2 transition-colors lg:w-72"
          >
            <Search className="w-4 h-4" />
            <span className="hidden lg:inline">Buscar cliente, CPF, contrato...</span>
            <kbd className="hidden lg:inline ml-auto text-[10px] bg-white border rounded px-1.5 py-0.5">Ctrl K</kbd>
          </button>

          <button data-testid="header-refresh-btn" onClick={doRefresh} title="Atualizar dados" className="p-1.5 sm:p-2 shrink-0 rounded-lg hover:bg-slate-100 text-slate-700">
            <RefreshCw className={`w-5 h-5 ${refreshing ? "animate-spin gold-text" : ""}`} />
          </button>
          <button data-testid="header-privacy-btn" onClick={() => setPrivacy(!privacy)} title={privacy ? "Mostrar valores" : "Ocultar valores (modo privacidade)"} className={`p-1.5 sm:p-2 shrink-0 rounded-lg hover:bg-slate-100 ${privacy ? "gold-text bg-amber-50" : "text-slate-700"}`}>
            {privacy ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>

          <Button
            data-testid="header-generate-link-btn"
            onClick={generateLink}
            size="sm"
            className="hidden sm:flex bg-onyx hover:bg-black text-white gap-2"
          >
            <Link2 className="w-4 h-4" /> Link de Análise
          </Button>
          {user?.empresa?.slug && (
            <Button data-testid="header-portal-link-btn" onClick={() => setPortalOpen(true)} size="sm" variant="outline" className="hidden sm:flex gap-2" title="Link do Portal do Cliente">
              <ExternalLink className="w-4 h-4 gold-text" /> Portal
            </Button>
          )}
          {portalOpen && <PortalLinkDialog onClose={() => setPortalOpen(false)} />}

          <InstallPWA className="hidden sm:flex" />

          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="header-notif-btn" className="relative p-1.5 sm:p-2 shrink-0 rounded-lg hover:bg-slate-100">
                <Bell className="w-5 h-5 text-slate-700" />
                {notif.nao_lidas > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                    {notif.nao_lidas}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="notif-popover">
              <div className="flex items-center justify-between p-3 border-b">
                <span className="font-semibold text-sm">Notificações</span>
                <div className="flex items-center gap-3">
                  <button onClick={markRead} className="text-xs gold-text hover:underline" data-testid="notif-mark-read">
                    Marcar lidas
                  </button>
                  {notif.items.length > 0 && (
                    <button onClick={clearNotif} className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="notif-clear">
                      <Trash2 className="w-3 h-3" /> Limpar
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto slim-scroll">
                {notif.items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Sem notificações</div>}
                {notif.items.map((n) => (
                  <div key={n.id} className={`p-3 border-b text-sm ${!n.lida ? "bg-amber-50/50" : ""}`}>
                    <div className="font-medium text-slate-800">{n.titulo}</div>
                    <div className="text-slate-500 text-xs mt-0.5 break-words">
                      {n.mensagem.replace(/https:\/\/wa\.me\/\S+/g, "").trim()}
                    </div>
                    <div className="flex items-center justify-between mt-1"><span className="text-slate-400 text-[10px]">{fmtDate(n.data)}</span><button type="button" onClick={() => apagar(n)} className="text-[11px] text-red-600 underline min-h-[32px] px-1" data-testid={`notif-apagar-${n.id}`}>Apagar</button></div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="header-user-menu" className="rounded-full shrink-0">
                <Avatar className="w-9 h-9 border border-gold/40">
                  <AvatarFallback className="bg-onyx text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-slate-500 font-normal capitalize">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/configuracoes")} data-testid="menu-settings">
                <Settings className="w-4 h-4 mr-2" /> Configurações
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setInstalarOpen(true)} data-testid="menu-instalar">
                <Smartphone className="w-4 h-4 mr-2" /> Como instalar o aplicativo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <InstalarDialog open={instalarOpen} onClose={() => setInstalarOpen(false)} />

        <main className="p-4 lg:p-6 pb-24 lg:pb-6 safe-x" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {refreshing && <div className="flex justify-center py-2 -mt-2 mb-2" data-testid="refresh-indicator"><Loader2 className="w-5 h-5 animate-spin gold-text" /></div>}
          <div>{children}</div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-onyx border-t border-white/10 flex items-stretch h-16 mobile-nav safe-x">
        {MOBILE_NAV.map((item) => {
          const Icon = ICONS[item.icon];
          const active = location.pathname.startsWith(item.path);
          return (
            <button
              key={item.path}
              data-testid={item.testid}
              onClick={() => navigate(item.path)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${active ? "gold-text" : "text-slate-400"}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          );
        })}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-nav-mais" className="flex-1 flex flex-col items-center justify-center gap-0.5 text-slate-400">
              <Menu className="w-5 h-5" />
              <span className="text-[10px] font-medium">Mais</span>
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-onyx border-white/10 w-80 max-w-[85vw] p-4 overflow-y-auto slim-scroll">
            <div className="py-3 flex justify-center" data-testid="sheet-brand"><Brand sheet /></div>
            <SidebarLinks user={user} role={user?.role} platformRole={user?.platform_role} onNavigate={() => setMoreOpen(false)} />
            <div className="mt-3 pt-3 border-t border-white/10 space-y-1" data-testid="mobile-links">
              <div className="px-3 text-[10px] uppercase tracking-wider text-slate-400">Links da empresa</div>
              <button onClick={() => { generateLink(); }} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5 w-full" data-testid="mobile-link-analise"><Link2 className="w-[18px] h-[18px] gold-text" /> Link de Análise (copiar)</button>
              {user?.empresa?.slug && <button onClick={() => { setMoreOpen(false); setPortalOpen(true); }} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5 w-full" data-testid="mobile-link-portal"><ExternalLink className="w-[18px] h-[18px] gold-text" /> Portal da empresa (abrir / copiar)</button>}
              <button onClick={() => { setMoreOpen(false); setInstalarOpen(true); }} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/5 w-full" data-testid="mobile-link-instalar"><Smartphone className="w-[18px] h-[18px] gold-text" /> Como instalar o aplicativo</button>
            </div>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 mt-4 rounded-lg text-sm text-red-400 hover:bg-white/5 w-full">
              <LogOut className="w-[18px] h-[18px]" /> Sair
            </button>
          </SheetContent>
        </Sheet>
      </nav>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
